import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowLeft, IconDownload, IconPencil, IconTrash } from '@tabler/icons-react'
import { isEmpty, startCase, toString, uniq } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { BaltoLogo } from '@/components/BaltoLogo'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { openModal } from '@/reducers/ui/ui.redux'
import { formatTime } from '@/utils/helpers'
import {
  fetchContactLists,
  fetchContacts,
  deleteContactList,
  deleteContact,
  exportContacts,
} from '@/reducers/outbound/outboundContactLists.actions'
import { setContacts } from '@/reducers/outbound/outboundContactLists.redux'

import './ContactListsPage.scss'
import { ContactListWizard } from './components/forms/ContactLists/CreateContactListWizard'
import { ContactFormModal } from './components/forms/Contacts/ContactFormModal'

const contactListColumns = (onSelectContactList) => [
  {
    accessor: 'name',
    label: 'Name',
    isSearchable: true,
    format: (value, row) => (
      <Button link onClick={() => onSelectContactList(row)}>
        {value}
      </Button>
    ),
  },
  {
    accessor: 'contactCount',
    label: 'Contacts',
    format: (value) => toString(value),
  },
  {
    accessor: 'campaigns',
    label: 'Campaigns',
    format: (value) => {
      if (isEmpty(value)) {
        return ''
      }
      if (value.length === 1) {
        return value[0].name
      }
      return (
        <InteractiveTooltip
          content={value.map((campaign) => (
            <div key={campaign.id}>{campaign.name}</div>
          ))}
        >
          <span>{value[0].name}...</span>
        </InteractiveTooltip>
      )
    },
  },
  {
    accessor: 'created_at',
    label: 'Created At',
    format: (value) => formatTime(value),
  },
]

const baseContactColumns = [
  { accessor: 'first_name', label: 'First Name', isSearchable: true },
  { accessor: 'last_name', label: 'Last Name', isSearchable: true },
  { accessor: 'phone_number', label: 'Phone Number', isSearchable: true },
  { accessor: 'timezone', label: 'Timezone' },
]

const CONTACTS_PER_PAGE = 100

export const ContactListsPage = () => {
  const dispatch = useDispatch()
  const { data, loading } = useSelector((state) => state.outboundContactLists)
  const { contactLists, contacts, contactsTotal } = data
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const [editingContactList, setEditingContactList] = useState(null)
  const [editingContact, setEditingContact] = useState(null)
  const [selectedContactList, setSelectedContactList] = useState(null)
  const [contactsPage, setContactsPage] = useState(1)

  const contactRows = useMemo(
    () =>
      contacts.map((contact) => ({
        ...contact,
        ...(contact.custom_fields || {}),
      })),
    [contacts]
  )

  const customFieldKeys = useMemo(
    () => uniq(contacts.flatMap((contact) => Object.keys(contact.custom_fields || {}))),
    [contacts]
  )

  const contactColumns = useMemo(() => {
    const customColumns = customFieldKeys.map((key) => ({
      accessor: key,
      label: startCase(key),
    }))

    return [
      ...baseContactColumns,
      ...customColumns,
      { accessor: 'created_at', label: 'Created At', format: (value) => formatTime(value) },
    ]
  }, [customFieldKeys])

  useEffect(() => {
    dispatch(fetchContactLists())
  }, [])

  useEffect(() => {
    dispatch(setContacts({ contacts: [], total: 0 }))
    setContactsPage(1)
    if (selectedContactList) {
      dispatch(fetchContacts(selectedContactList.id, { limit: CONTACTS_PER_PAGE, offset: 0 }))
    }
  }, [selectedContactList])

  useEffect(() => {
    if (selectedContactList) {
      const offset = (contactsPage - 1) * CONTACTS_PER_PAGE
      dispatch(fetchContacts(selectedContactList.id, { limit: CONTACTS_PER_PAGE, offset }))
    }
  }, [contactsPage])

  const handleEditContactList = (contactList) => {
    setEditingContactList(contactList)
    dispatch(openModal('editContactList'))
  }

  const handleDeleteContactList = (contactListId) => {
    dispatch(deleteContactList(contactListId))
  }

  const refreshContacts = () => {
    const offset = (contactsPage - 1) * CONTACTS_PER_PAGE
    dispatch(fetchContacts(selectedContactList.id, { limit: CONTACTS_PER_PAGE, offset }))
  }

  const handleEditContact = (contact) => {
    setEditingContact(contact)
    dispatch(openModal('editContact'))
  }

  const handleDeleteContact = async (contactId) => {
    const deleted = await dispatch(deleteContact(contactId))
    if (deleted) {
      // Deleting the last contact on a non-first page would re-fetch a now-empty
      // offset; step back a page instead (the contactsPage effect re-fetches).
      if (contacts.length === 1 && contactsPage > 1) {
        setContactsPage(contactsPage - 1)
      } else {
        refreshContacts()
      }
    }
  }

  const handleBack = () => {
    setSelectedContactList(null)
  }

  const rows = contactLists.map((contactList) => ({
    ...contactList,
    contactCount: contactList.contacts ? contactList.contacts.length : 0,
  }))

  const actions = [
    {
      label: 'Edit',
      icon: <IconPencil />,
      fn: (_event, row) => {
        handleEditContactList(row)
      },
    },
    {
      label: 'Delete',
      trigger: (row) => {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconTrash />}
            buttonProps={{ basic: true, small: true, square: true }}
            modalId={`contactLists/delete/${row.id}`}
            modalTitle={`Delete "${row.name}"`}
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>
                Are you sure you want to delete this contact list? This action cannot be undone.
              </p>
            }
            form={<ModalActions onSubmit={() => handleDeleteContactList(row.id)} isDelete />}
          />
        )
      },
    },
  ]

  const contactActions = [
    {
      label: 'Edit',
      icon: <IconPencil />,
      fn: (_event, row) => {
        handleEditContact(row)
      },
    },
    {
      label: 'Delete',
      trigger: (row) => {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconTrash />}
            buttonProps={{ basic: true, small: true, square: true }}
            modalId={`contacts/delete/${row.id}`}
            modalTitle="Delete Contact"
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>Are you sure you want to delete this contact? This action cannot be undone.</p>
            }
            form={<ModalActions onSubmit={() => handleDeleteContact(row.id)} isDelete />}
          />
        )
      },
    },
  ]

  if (selectedContactList) {
    return (
      <>
        <PageHeader
          title={
            <div className="flex-align-center medium-gap">
              <Button small square onClick={handleBack} data-testid="back-button">
                <IconArrowLeft />
              </Button>
              {selectedContactList.name}
            </div>
          }
          actions={
            <>
              <Button
                square
                aria-label="Export contacts to CSV"
                onClick={() =>
                  dispatch(exportContacts(selectedContactList.id, selectedContactList.name))
                }
                tooltip="Export contacts to CSV"
                disabledTooltip="Exporting contacts…"
                loading={loading.csvExport}
                data-testid="export-contacts-button"
              >
                <IconDownload />
              </Button>
              <Button brand onClick={() => dispatch(openModal('createContact'))}>
                Add Contact
              </Button>
            </>
          }
        />
        {currentlyOpenModalId === 'createContact' && (
          <ContactFormModal
            contactListId={selectedContactList.id}
            customFieldKeys={customFieldKeys}
            onSuccess={refreshContacts}
          />
        )}
        {currentlyOpenModalId === 'editContact' && (
          <ContactFormModal
            contact={editingContact}
            contactListId={selectedContactList.id}
            customFieldKeys={customFieldKeys}
            onSuccess={refreshContacts}
          />
        )}
        <Card className="no-padding">
          <AdvancedTable
            rows={contactRows}
            columns={contactColumns}
            actions={contactActions}
            stickyAction
            loading={loading.contacts}
            defaultOrderBy="created_at"
            defaultOrder="desc"
            index="id"
          />
          {contactsTotal > 0 && (
            <AdvancedTablePagination
              activePage={contactsPage}
              rowsPerPage={CONTACTS_PER_PAGE}
              count={contactsTotal}
              totalPages={Math.ceil(contactsTotal / CONTACTS_PER_PAGE)}
              setActivePage={setContactsPage}
              label="contact"
            />
          )}
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Contact Lists"
        actions={
          <Button brand onClick={() => dispatch(openModal('createContactList'))}>
            Create Contact List
          </Button>
        }
      />
      {currentlyOpenModalId === 'createContactList' && <ContactListWizard />}
      {currentlyOpenModalId === 'editContactList' && (
        <ContactListWizard contactList={editingContactList} />
      )}
      {!loading.contactLists && isEmpty(rows) ? (
        <div className="outbound-empty-state">
          <div className="outbound-empty-state__logo">
            <BaltoLogo width="200" height="150" fill="var(--color-neutral)" />
          </div>
          <div className="outbound-empty-state__title">Create your first contact list</div>
          <p className="outbound-empty-state__description">
            Organize your contacts into lists to use in outbound campaigns. Upload a CSV to get
            started.
          </p>
        </div>
      ) : (
        <Card className="no-padding">
          <AdvancedTable
            rows={rows}
            columns={contactListColumns(setSelectedContactList)}
            actions={actions}
            loading={loading.contactLists}
            defaultOrderBy="created_at"
            defaultOrder="desc"
            index="id"
            pagination
            stickyAction
          />
        </Card>
      )}
    </>
  )
}
